<script lang="ts" setup>
import type { CartFragment, StoreStockStatusFragment, StoreStockStatusStoreFragment } from '#graphql-operations'

const emit = defineEmits(['showStoreStock'])

// Extract cart item type from CartFragment with NonNullable for optional array
type CartItem = NonNullable<NonNullable<CartFragment['items']>[number]>

export interface Props {
  item: CartItem
  editable?: boolean
}

const props = withDefaults(defineProps<Props>(), { editable: false })
const { item } = toRefs(props)
const { allocatedStore, getItemStoreStock } = useStorelocator()
const { updateCartItem, removeCartItem, addedToCart, pending } = useCart()
const { public: config } = useRuntimeConfig()
const quantity = ref<number>(item.value?.quantity || 0)

const hasErrors = computed<boolean>(() =>
  !!item.value?.errors,
)

const simpleProduct = computed(() =>
  item.value.configured_variant || item.value.product,
)

const unitPrice = computed(() => {
  return {
    finalPrice: simpleProduct.value?.price_range?.minimum_price?.final_price?.value,
    regularPrice: simpleProduct.value?.price_range?.minimum_price?.regular_price?.value,
  }
})

const totalPrice = computed(() => {
  return {
    finalPrice: item.value.prices.row_total_including_tax.value,
    regularPrice: simpleProduct.value?.price_range?.minimum_price?.regular_price?.value * item.value.quantity,
  }
})

const image = computed<string>(() => {
  if (simpleProduct.value?.image?.url && !simpleProduct.value?.image?.url.includes('placeholder')) {
    return simpleProduct.value?.image?.url?.replace('FD/-/', 'OQ75_RM170,1066,255,255,255/-/')
  }
  if (item.value.product.image?.url && !item.value.product.image?.url.includes('placeholder')) {
    return item.value?.product?.image?.url?.replace('FD/-/', 'OQ75_RM170,1066,255,255,255/-/')
  }
  return `${config.xcdnUrl}OQ75_RM170,1066,255,255,255/-/catalog/product/placeholder/default/Product.FallBack.jpg`
})

const { removeFromCart } = useDataLayer()
const updateItem = async () => {
  const oldQty = item.value.quantity
  await updateCartItem({
    cart_item_id: +item.value.id,
    quantity: quantity.value,
  }, true)
  if (addedToCart.value?.length && addedToCart.value.length > 0) {
    quantity.value = +item.value.quantity
  }
  const newQty = item.value.quantity
  if (oldQty > newQty) {
    removeFromCart({ ...item.value, quantity: oldQty - newQty })
  }
}

const removeItem = async () => {
  await removeCartItem(+item.value.id)
  removeFromCart(item.value)
}

const debounceUpdateItem = useDebounceFn(() => {
  updateItem()
}, 150, { maxWait: 600 })

const storeStock = computed<StoreStockStatusFragment | null>(() => getItemStoreStock(String(props.item?.id)) || null)

const qtyStoresWithStock = computed<number>(() => {
  return storeStock.value?.stores?.filter(item => item?.status === 'in-stock')?.length || 0
})

const allocatedStoreStock = computed<StoreStockStatusStoreFragment | null>(() => {
  if (!allocatedStore.value) {
    return null
  }
  return storeStock.value?.stores?.find(item => item?.code === allocatedStore.value?.code) || null
})

const maxStockInStore = computed(() => {
  if (storeStock.value?.status === 'all-backorder') {
    return 10
  }
  return storeStock.value?.stores?.reduce((acc: number, item: any) => item.totalStock > acc ? item.totalStock : acc, 1) || 1
})

const maxSaleQty = computed<number>(() =>
  Math.min(maxStockInStore.value, 100, simpleProduct.value?.stockItem?.max_sale_qty || 100, simpleProduct.value?.stock_left || 1),
)
</script>

<template>
  <div class="cart-line__product">
    <div class="cart-line__product-visual">
      <AppLink :to="item.product.product?.url">
        <img
          :src="image"
          :alt="item.product.name"
        >
      </AppLink>
      <div
        class="cart-line__product-labels"
      >
        <ProductLabelSale :product="item.product.product" />
        <ProductLabelMarketing :product="item.product.product" />
      </div>
    </div>

    <div class="cart-line__product-content">
      <UiHeading
        :force-tag="'div'"
        class="cart-line__product-name"
      >
        <AppLink
          :to="item.product.product?.url"
          class="cart-line__product-link"
        >
          <template v-if="item.product?.product?.mainTitle">
            {{ item.product.product.mainTitle }}
          </template>
        </AppLink>
        <span
          v-if="item.product?.product?.subTitle"
          class="cart-line__product-brand"
        >
          {{ item.product.product.subTitle }}
        </span>
      </UiHeading>

      <div
        class="cart-line__unit-price"
      >
        <div class="cart-line__units">
          {{ $t('Quantity:') }} {{ quantity }}
        </div>
        <UiPrice
          v-if="quantity > 1"
          :prices="unitPrice"
          class="cart-line__unit-price-value"
        >
          <template #suffix>
            <span class="cart-line__unit-price-suffix">
              {{ '/' }}{{ $t('each') }}
            </span>
          </template>
        </UiPrice>
      </div>
      <table
        v-if="item?.configurable_options"
        class="cart-line__product-data"
      >
        <tr
          v-for="(option, index) in item.configurable_options"
          :key="index"
        >
          <td>
            {{ option?.value_label }}
          </td>
        </tr>
        <tr v-if="!editable">
          <td>{{ $t('Quantity') }} {{ quantity }}</td>
        </tr>
      </table>

      <div
        v-if="editable"
        class="cart-line__delivery-promise"
      >
        <ul>
          <ProductLabelDeliveryPromise
            v-slot="{ inStock, hasDeliveryTime, weeks }"
            :product="item.product.product"
          >
            <li
              v-if="inStock || hasDeliveryTime || item?.product?.product?.isPickupOnly"
              class="cart-line__delivery-promise-item cart-line__delivery-promise--delivery"
            >
              <span
                :class="`availability__online--${(inStock && !item?.product?.product?.isPickupOnly) ? 'true' : 'false'}`"
                class="cart-line__availability-bullet"
              />
              <span v-if="item?.product?.product?.isPickupOnly">{{ $t('Delivery not available') }}</span>
              <span v-else-if="inStock">{{ $t('Quick delivery') }}</span>
              <span v-else-if="hasDeliveryTime">{{ $t('Expected delivery time from: {weeks} weeks', { weeks }) }}</span>
            </li>
            <li
              v-if="!hasDeliveryTime"
              class="cart-line__delivery-promise-item cart-line__delivery-promise--pickup"
            >
              <span
                v-if="allocatedStoreStock"
                :class="[`availability__store--${allocatedStoreStock.status}`]"
                class="cart-line__availability-bullet"
              />
              <span
                v-else-if="qtyStoresWithStock"
                :class="[`availability__store--no-select`, `availability__store--in-stock`]"
                class="cart-line__availability-bullet"
              />
              <span
                v-else-if="storeStock?.status?.includes('backorder')"
                :class="[`availability__store--backorder`]"
                class="cart-line__availability-bullet"
              />
              <span v-if="allocatedStoreStock?.status === 'in-stock'">{{ $t('In stock at') }} <button @click="emit('showStoreStock', storeStock)">{{ allocatedStore?.name }}</button></span>
              <span v-else-if="allocatedStoreStock?.status === 'all-backorder'">{{ $tc('all-backorder', allocatedStoreStock?.deliveryTime || 0) }} bij <button @click="emit('showStoreStock', storeStock)">{{ allocatedStore?.name }}</button></span>
              <span v-else-if="allocatedStoreStock?.status?.includes('out-of-stock')">{{ $t('all-out-of-stock') }} bij <button @click="emit('showStoreStock', storeStock)">{{ allocatedStore?.name }}</button></span>
              <span v-else-if="qtyStoresWithStock">{{ $t('Order and pickup') }}. <button @click="emit('showStoreStock', storeStock)">{{ $t('Select your store') }}</button></span>
              <span v-else-if="storeStock?.status === 'all-backorder'">{{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}</span>
            </li>
          </ProductLabelDeliveryPromise>
        </ul>
      </div>

      <div class="cart-line__notices">
        <button
          v-if="item?.product?.product?.isPickupOnly"
          class="cart-line__notice cart-line__product-pickup-only"
        >
          {{ $t('This product is only available for pickup') }}
          <NuxtIcon name="icon:info-filled" />
        </button>
        <button
          v-if="item?.product?.stock_status === 'OUT_OF_STOCK'"
          class="cart-line__notice cart-line__notice-oos"
        >
          {{ $t('Article is out of stock') }}
          <NuxtIcon name="icon:info-filled" />
        </button>
      </div>
    </div>

    <div
      v-if="editable"
      class="cart-line__product-update"
    >
      <div class="cart-line__product-quantity">
        <InputNumberPlusMinus
          v-model="quantity"
          :min="0"
          :max="maxSaleQty"
          :step="1"
          :disabled="pending || !maxSaleQty"
          @change="debounceUpdateItem"
        />
      </div>
      <button
        class="cart-line__product-remove"
        :disabled="pending"
        :aria-label="$t('Remove') + ' ' + item.product.name"
        @click.prevent="removeItem"
      >
        <NuxtIcon name="icon:trash" />
      </button>
    </div>
    <UiPrice
      class="cart-line__product-totalprice"
      :prices="totalPrice"
    />
    <div
      v-if="hasErrors && !(item?.product?.stock_status === 'OUT_OF_STOCK')"
      class="cart-line__product-error"
    >
      <p
        v-for="(error, index) in item.errors"
        :key="`error-${index}`"
      >
        {{ error?.message }}
      </p>
    </div>
  </div>
</template>

<style>
@import url("~layers/components/Cart/Line.css");
</style>
